import React, { useEffect, useState } from 'react';
import {
    FiTruck, FiCopy, FiExternalLink, FiMapPin, FiPackage,
    FiInbox, FiClock, FiCheckCircle,
} from 'react-icons/fi';
import { getMinhasSolicitacoesApi } from '../../Api';
import { notify } from '../../components/Notify';

// Página pública (do estudante logado) de acompanhamento da carteirinha física.
// Mostra cada solicitação de impressão do aluno; quando há código de rastreio
// (etiqueta emitida/postada = "em percurso"), exibe o código + link dos Correios.

// O site oficial dos Correios é uma SPA e não aceita o código direto na URL —
// por isso mostramos o código com botão de copiar e o aluno cola no site.
const URL_RASTREIO_CORREIOS = 'https://rastreamento.correios.com.br/app/index.php';

const SITUACOES = {
    em_transito:          { label: 'Em trânsito',          cor: '#047857', bg: '#ECFDF5', borda: '#A7F3D0', Icon: FiTruck },
    entregue:             { label: 'Entregue',             cor: '#1D4ED8', bg: '#EFF6FF', borda: '#BFDBFE', Icon: FiCheckCircle },
    preparando:           { label: 'Preparando envio',     cor: '#B45309', bg: '#FFFBEB', borda: '#FDE68A', Icon: FiPackage },
    aguardando_endereco:  { label: 'Aguardando endereço',  cor: '#B45309', bg: '#FFFBEB', borda: '#FDE68A', Icon: FiMapPin },
    aguardando_aprovacao: { label: 'Em análise',           cor: '#475569', bg: '#F1F5F9', borda: '#E2E8F0', Icon: FiClock },
};

const MENSAGEM_SEM_CODIGO = {
    aguardando_endereco: 'Preencha o endereço de entrega da sua carteirinha para liberarmos o envio. O código de rastreio aparece aqui assim que postarmos.',
    aguardando_aprovacao: 'Sua carteirinha está em análise. Assim que for aprovada e postada, o código de rastreio aparece aqui.',
    preparando: 'Tudo certo! Estamos preparando a postagem. O código de rastreio aparece aqui assim que ela for enviada.',
};

function formatarDataHora(iso) {
    if (!iso) return '';
    const d = new Date(String(iso).replace(' ', 'T'));
    if (isNaN(d.getTime())) return '';
    const p = (n) => String(n).padStart(2, '0');
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

export default function Rastreamento() {
    const [itens, setItens] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const ctrl = new AbortController();
        getMinhasSolicitacoesApi({ signal: ctrl.signal })
            .then((res) => setItens(res.data?.itens || []))
            .catch((err) => {
                if (err?.name !== 'CanceledError' && err?.code !== 'ERR_CANCELED') {
                    notify('Não foi possível carregar o rastreamento.', 'error');
                }
            })
            .finally(() => setLoading(false));
        return () => ctrl.abort();
    }, []);

    const copiar = async (codigo) => {
        try {
            await navigator.clipboard.writeText(codigo);
            notify('Código copiado!', 'success');
        } catch (_) {
            notify('Não foi possível copiar o código.', 'error');
        }
    };

    return (
        <div style={styles.wrap}>
            <header style={styles.header}>
                <h1 style={styles.title}>
                    <FiTruck size={20} color="#059669" /> Rastreamento
                </h1>
                <p style={styles.subtitle}>
                    Acompanhe o envio da sua carteirinha física. Quando ela for postada,
                    o código de rastreio aparece aqui.
                </p>
            </header>

            {loading ? (
                <div style={styles.loading}>
                    <div style={styles.spinner} />
                    <p style={styles.loadingTxt}>Carregando rastreamento…</p>
                    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
                </div>
            ) : itens.length === 0 ? (
                <div style={styles.vazio}>
                    <FiInbox size={38} color="#CBD5E1" />
                    <div style={styles.vazioTitulo}>Nenhuma carteirinha física</div>
                    <div style={styles.vazioTexto}>
                        Você ainda não solicitou uma carteirinha física. Quando solicitar,
                        o acompanhamento da entrega aparece aqui.
                    </div>
                </div>
            ) : (
                <div style={styles.lista}>
                    {itens.map((it) => (
                        <Card key={it.id} item={it} onCopiar={copiar} />
                    ))}
                </div>
            )}
        </div>
    );
}

function Card({ item, onCopiar }) {
    const sit = SITUACOES[item.situacao] || SITUACOES.aguardando_aprovacao;
    const SitIcon = sit.Icon;
    const temCodigo = !!item.codigoRastreio;
    const destino = [item.cidade, item.estado].filter(Boolean).join(' - ');

    return (
        <div style={styles.card}>
            <div style={styles.cardTopo}>
                <span style={{ ...styles.badge, color: sit.cor, background: sit.bg, borderColor: sit.borda }}>
                    <SitIcon size={12} /> {sit.label}
                </span>
                <span style={styles.ano}>Carteirinha {item.ano}</span>
            </div>

            <div style={styles.meta}>
                {item.modalidade && <span style={styles.metaItem}><FiPackage size={12} /> {item.modalidade}</span>}
                {destino && <span style={styles.metaItem}><FiMapPin size={12} /> {destino}</span>}
            </div>

            {temCodigo ? (
                <div style={styles.rastreioBox}>
                    <div style={styles.rastreioLabel}>Código de rastreio</div>
                    <div style={styles.codigoRow}>
                        <span style={styles.codigo}>{item.codigoRastreio}</span>
                        <button type="button" style={styles.btnCopiar} onClick={() => onCopiar(item.codigoRastreio)}>
                            <FiCopy size={13} /> Copiar
                        </button>
                    </div>
                    <a
                        href={URL_RASTREIO_CORREIOS}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={styles.btnCorreios}
                    >
                        <FiExternalLink size={14} /> Acompanhar nos Correios
                    </a>
                    <div style={styles.dica}>
                        Copie o código acima e cole no site dos Correios para ver a localização.
                    </div>

                    {item.eventos && item.eventos.length > 0 && (
                        <div style={styles.timeline}>
                            <div style={styles.timelineTitulo}>Movimentação</div>
                            {item.eventos.map((ev, i) => (
                                <div key={i} style={styles.evento}>
                                    <span style={{ ...styles.evtDot, ...(i === 0 ? styles.evtDotAtivo : {}) }} />
                                    <div style={{ minWidth: 0 }}>
                                        <div style={styles.evtDesc}>{ev.descricao}</div>
                                        {(ev.local || ev.data) && (
                                            <div style={styles.evtMeta}>
                                                {[ev.local, formatarDataHora(ev.data)].filter(Boolean).join(' • ')}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ) : (
                <div style={styles.aviso}>
                    {MENSAGEM_SEM_CODIGO[item.situacao] || 'Acompanhe por aqui — o código de rastreio aparece assim que a carteirinha for postada.'}
                </div>
            )}
        </div>
    );
}

const styles = {
    wrap: { maxWidth: 720, margin: '0 auto' },
    header: { marginBottom: 20 },
    title: {
        display: 'flex', alignItems: 'center', gap: 10,
        fontSize: '1.5rem', fontWeight: 800, color: '#0F172A', margin: '0 0 6px',
    },
    subtitle: { margin: 0, color: '#64748B', fontSize: '0.92rem', lineHeight: 1.5 },

    estado: { padding: '40px 0', textAlign: 'center', color: '#64748B' },
    loading: {
        padding: '4rem 1rem', textAlign: 'center',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem',
    },
    loadingTxt: { color: '#64748B', margin: 0, fontSize: '0.92rem' },
    spinner: {
        width: 32, height: 32,
        border: '3px solid #E2E8F0',
        borderTopColor: '#059669',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
    vazio: {
        display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
        gap: 8, padding: '48px 20px', background: '#F8FAFC',
        border: '1px solid #E2E8F0', borderRadius: 14,
    },
    vazioTitulo: { fontWeight: 700, color: '#0F172A', fontSize: '1rem' },
    vazioTexto: { color: '#64748B', fontSize: '0.88rem', maxWidth: 380, lineHeight: 1.5 },

    lista: { display: 'flex', flexDirection: 'column', gap: 14 },
    card: {
        background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 14,
        padding: '16px 18px', boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
    },
    cardTopo: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 10, marginBottom: 10, flexWrap: 'wrap',
    },
    badge: {
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '4px 10px', borderRadius: 999, border: '1px solid',
        fontSize: '0.74rem', fontWeight: 700,
    },
    ano: { fontSize: '0.8rem', color: '#64748B', fontWeight: 600 },
    meta: { display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 12 },
    metaItem: {
        display: 'inline-flex', alignItems: 'center', gap: 5,
        fontSize: '0.82rem', color: '#475569',
    },

    rastreioBox: {
        background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 10, padding: 14,
    },
    rastreioLabel: {
        fontSize: '0.7rem', fontWeight: 700, color: '#64748B',
        textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6,
    },
    codigoRow: {
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12,
    },
    codigo: {
        fontFamily: "'JetBrains Mono', monospace", fontSize: '1.05rem', fontWeight: 700,
        color: '#0F172A', letterSpacing: '0.04em',
    },
    btnCopiar: {
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '6px 11px', background: '#FFFFFF', color: '#047857',
        border: '1px solid #A7F3D0', borderRadius: 8, cursor: 'pointer',
        fontSize: '0.78rem', fontWeight: 600, fontFamily: 'inherit',
    },
    btnCorreios: {
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7,
        width: '100%', boxSizing: 'border-box', padding: '11px',
        background: 'linear-gradient(135deg,#059669,#10B981)', color: '#FFFFFF',
        borderRadius: 9, textDecoration: 'none', fontSize: '0.88rem', fontWeight: 700,
    },
    dica: { marginTop: 8, fontSize: '0.76rem', color: '#64748B', textAlign: 'center' },

    timeline: { marginTop: 14, paddingTop: 12, borderTop: '1px solid #E2E8F0' },
    timelineTitulo: {
        fontSize: '0.7rem', fontWeight: 700, color: '#64748B',
        textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10,
    },
    evento: { display: 'flex', gap: 10, paddingBottom: 12 },
    evtDot: {
        width: 9, height: 9, borderRadius: '50%', background: '#CBD5E1',
        marginTop: 4, flexShrink: 0,
    },
    evtDotAtivo: { background: '#059669', boxShadow: '0 0 0 3px #D1FAE5' },
    evtDesc: { fontSize: '0.84rem', color: '#0F172A', fontWeight: 600, lineHeight: 1.35 },
    evtMeta: { fontSize: '0.74rem', color: '#94A3B8', marginTop: 2 },

    aviso: {
        background: '#F8FAFC', border: '1px dashed #CBD5E1', borderRadius: 10,
        padding: '12px 14px', fontSize: '0.85rem', color: '#475569', lineHeight: 1.5,
    },
};
